var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _dec9, _class, _class2;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
import { set, computed } from '@ember/object';
import { CountryAddressData } from '@square/dashboard-protos/models/squareup/address/country_data';
import Enum from '@square/dashboard-utilities/utils/enum';

/**
 * CountryAddressDataLocalizer
 *
 * A utility class that allows obtaining localized string from `CountryAddressData`,
 * often for use in address components. Examples include: locality, sublocality, postal code, etc.
 *
 * @property {Service} i18n - An instance of the i18n service.
 * @property {CountryAddressData} addressData - Address metadata used to get localized address component name.
 * @property {Object} [labelOverrides={}] - A hash of translated strings used to override values returned by
 *   `CountryAddressDataLocalizer`.
 *
 *   It should take the form:
 *   {
 *     [CountryAddressDataLocalizer.AddressField.<field>]: <override string>,
 *     [CountryAddressDataLocalizer.AddressField.<field>]: <override string>,
 *     ...
 *   }.
 */
let CountryAddressDataLocalizer = (_dec = computed('addressData.admin1Label', 'i18n', 'labelOverrides.ADMIN1'), _dec2 = computed('addressData.admin1Values', 'countryCodeValue'), _dec3 = computed('addressData.localityLabel', 'i18n', 'labelOverrides.LOCALITY'), _dec4 = computed('addressData.postalCodeLabel', 'i18n', 'labelOverrides.POSTAL_CODE'), _dec5 = computed('addressData.sortingCodeLabel', 'i18n', 'labelOverrides.SORTING_CODE'), _dec6 = computed('addressData.sublocalityLabel', 'i18n', 'labelOverrides.SUBLOCALITY'), _dec7 = computed('i18n', 'labelOverrides.STREET1'), _dec8 = computed('i18n', 'labelOverrides.STREET2'), _dec9 = computed('addressData.countryCode'), (_class = (_class2 = class CountryAddressDataLocalizer {
  constructor(addressData, i18n, labelOverrides = null) {
    _defineProperty(this, "i18n", null);
    _defineProperty(this, "addressData", null);
    _defineProperty(this, "labelOverrides", null);
    set(this, 'i18n', i18n);
    set(this, 'addressData', addressData);
    set(this, 'labelOverrides', labelOverrides);
  }

  /**
   * @returns {String} A label representing the administration level of the address.
   */
  get admin1Name() {
    if (this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.ADMIN1)) {
      return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.ADMIN1);
    }
    switch (this.addressData.admin1Label) {
      case CountryAddressData.Admin1Scope.Admin1Label.AREA:
        return this.i18n.t('addressService.placeholder.admin1.area');
      case CountryAddressData.Admin1Scope.Admin1Label.DEPARTMENT:
        return this.i18n.t('addressService.placeholder.admin1.department');
      case CountryAddressData.Admin1Scope.Admin1Label.DISTRICT:
        return this.i18n.t('addressService.placeholder.admin1.district');
      case CountryAddressData.Admin1Scope.Admin1Label.DO_SI:
        return this.i18n.t('addressService.placeholder.admin1.doSi');
      case CountryAddressData.Admin1Scope.Admin1Label.EMIRATE:
        return this.i18n.t('addressService.placeholder.admin1.emirate');
      case CountryAddressData.Admin1Scope.Admin1Label.ISLAND:
        return this.i18n.t('addressService.placeholder.admin1.island');
      case CountryAddressData.Admin1Scope.Admin1Label.OBLAST:
        return this.i18n.t('addressService.placeholder.admin1.oblast');
      case CountryAddressData.Admin1Scope.Admin1Label.PARISH:
        return this.i18n.t('addressService.placeholder.admin1.parish');
      case CountryAddressData.Admin1Scope.Admin1Label.PREFECTURE:
        return this.i18n.t('addressService.placeholder.admin1.prefecture');
      case CountryAddressData.Admin1Scope.Admin1Label.PROVINCE:
        return this.i18n.t('addressService.placeholder.admin1.province');
      case CountryAddressData.Admin1Scope.Admin1Label.STATE:
        return this.i18n.t('addressService.placeholder.admin1.state');
      case CountryAddressData.Admin1Scope.Admin1Label.COUNTY:
        return this.i18n.t('addressService.placeholder.admin1.county');
      default:
        return this.i18n.t('addressService.placeholder.admin1.default');
    }
  }

  /**
   * @returns {Object[]} Objects representing the administration values of the address.
   */
  get admin1Content() {
    return this.addressData.admin1Values?.map(item => {
      if (this.countryCodeValue === 'JP') {
        return {
          value: item.code,
          label: item.name
        };
      } else {
        return {
          value: item?.value || item.name,
          label: item.name
        };
      }
    });
  }

  /**
   * @returns {String} A label representing the locality of the address.
   */
  get localityName() {
    if (this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.LOCALITY)) {
      return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.LOCALITY);
    }
    switch (this.addressData.localityLabel) {
      case CountryAddressData.LocalityScope.LocalityLabel.CITY:
        return this.i18n.t('addressService.placeholder.locality.city');
      case CountryAddressData.LocalityScope.LocalityLabel.DISTRICT:
        return this.i18n.t('addressService.placeholder.locality.district');
      case CountryAddressData.LocalityScope.LocalityLabel.POST_TOWN:
        return this.i18n.t('addressService.placeholder.locality.postTown');
      case CountryAddressData.LocalityScope.LocalityLabel.SUBURB:
        return this.i18n.t('addressService.placeholder.locality.suburb');
      case CountryAddressData.LocalityScope.LocalityLabel.TOWN_CITY:
        return this.i18n.t('addressService.placeholder.locality.townCity');
      default:
        return this.i18n.t('addressService.placeholder.locality.default');
    }
  }

  /**
   * @returns {String} A label representing the postal code of the address.
   */
  get postalCodeName() {
    if (this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.POSTAL_CODE)) {
      return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.POSTAL_CODE);
    }
    switch (this.addressData.postalCodeLabel) {
      case CountryAddressData.PostalCodeScope.PostalCodeLabel.POSTAL:
        return this.i18n.t('addressService.placeholder.postalCode.postal');
      case CountryAddressData.PostalCodeScope.PostalCodeLabel.EIRCODE:
        return this.i18n.t('addressService.placeholder.postalCode.eirCode');
      case CountryAddressData.PostalCodeScope.PostalCodeLabel.PIN:
        return this.i18n.t('addressService.placeholder.postalCode.pin');
      case CountryAddressData.PostalCodeScope.PostalCodeLabel.ZIP:
        return this.i18n.t('addressService.placeholder.postalCode.zip');
      default:
        return this.i18n.t('addressService.placeholder.postalCode.default');
    }
  }

  /**
   * @returns {String} A label representing the sorting code of the address.
   */
  get sortingCodeName() {
    if (this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.SORTING_CODE)) {
      return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.SORTING_CODE);
    }
    switch (this.addressData.sortingCodeLabel) {
      case CountryAddressData.SortingCodeScope.SortingCodeLabel.CEDEX:
        return this.i18n.t('addressService.placeholder.sortingCode.cedex');
      default:
        return this.i18n.t('addressService.placeholder.sortingCode.default');
    }
  }

  /**
   * @returns {String} A label representing the sub-locality of the address.
   */
  get sublocalityName() {
    if (this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.SUBLOCALITY)) {
      return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.SUBLOCALITY);
    }
    switch (this.addressData.sublocalityLabel) {
      case CountryAddressData.SublocalityScope.SublocalityLabel.SUBURB:
        return this.i18n.t('addressService.placeholder.sublocality.suburb');
      case CountryAddressData.SublocalityScope.SublocalityLabel.DISTRICT:
        return this.i18n.t('addressService.placeholder.sublocality.district');
      case CountryAddressData.SublocalityScope.SublocalityLabel.NEIGHBORHOOD:
        return this.i18n.t('addressService.placeholder.sublocality.neighborhood');
      case CountryAddressData.SublocalityScope.SublocalityLabel.TOWNLAND:
        return this.i18n.t('addressService.placeholder.sublocality.townland');
      case CountryAddressData.SublocalityScope.SublocalityLabel.VILLAGE_TOWNSHIP:
        return this.i18n.t('addressService.placeholder.sublocality.villageTownship');
      case CountryAddressData.SublocalityScope.SublocalityLabel.TOWN:
        return this.i18n.t('addressService.placeholder.sublocality.town');
      default:
        return this.i18n.t('addressService.placeholder.sublocality.default');
    }
  }

  /**
   * @returns {String} A label representing the first line of the street name of the address.
   */
  get street1Name() {
    return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.STREET1) ?? this.i18n.t('addressService.placeholder.addressLine1');
  }

  /**
   * @returns {String} A label representing the second line of the street name of the address.
   */
  get street2Name() {
    return this.labelOverrideForField(CountryAddressDataLocalizer.AddressField.STREET2) ?? this.i18n.t('addressService.placeholder.addressLine2');
  }

  /**
   Returns a label override for the given address field, if one exists. By definition, this method assumes that an
   override has been specified if, and only if, the value of `this.labelOverrides[field]` is not `null` or `undefined`.
   Thus, if it is desirable to show a blank field, an empty String should be used.
    @param {String} field - An `AddressField` entry specifying which address field to check `labelOverrides` for.
   @returns {?String} A string representing a label override if one has been defined in `labelOverrides` for `field`.
  */
  labelOverrideForField(field) {
    return this.labelOverrides ? this.labelOverrides[field] : null;
  }
  get countryCodeValue() {
    return this.addressData.countryCode;
  }
}, _defineProperty(_class2, "AddressField", new Enum(['ADMIN1', 'LOCALITY', 'POSTAL_CODE', 'SORTING_CODE', 'SUBLOCALITY', 'STREET1', 'STREET2'])), _class2), (_applyDecoratedDescriptor(_class.prototype, "admin1Name", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "admin1Name"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "admin1Content", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "admin1Content"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "localityName", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "localityName"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "postalCodeName", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "postalCodeName"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sortingCodeName", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "sortingCodeName"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "sublocalityName", [_dec6], Object.getOwnPropertyDescriptor(_class.prototype, "sublocalityName"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "street1Name", [_dec7], Object.getOwnPropertyDescriptor(_class.prototype, "street1Name"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "street2Name", [_dec8], Object.getOwnPropertyDescriptor(_class.prototype, "street2Name"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "countryCodeValue", [_dec9], Object.getOwnPropertyDescriptor(_class.prototype, "countryCodeValue"), _class.prototype)), _class));
export { CountryAddressDataLocalizer as default };